import { useState } from 'react';
import { Alert, Box } from '@mui/material';
import { CancelOutlined } from '@mui/icons-material';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
// Our Components
// Services
// Assets
import { ReactComponent as UploadIcon } from 'assets/transactionsSection/upload-receipt-icon.svg';
import { useField } from 'formik';

const MAX_NUMBER_OF_RECEIPT_FILES = 5;
const RECEIPT_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const RECEIPT_MAX_FILE_SIZE = 1024 * 1024 * 25; // 25MB
const receiptFileValidation = Yup.mixed()
    .notRequired()
    .test('fileFormat', 'Invalid file type. Only jpeg, png, pdf are allowed', receiptFile =>
        !receiptFile ? true : receiptFile && RECEIPT_MIME_TYPES.includes(receiptFile.type)
    )
    .test('fileSize', 'File is too big. Max file size is 25MB', receiptFile =>
        !receiptFile ? true : receiptFile && receiptFile.size <= RECEIPT_MAX_FILE_SIZE
    );
const ReceiptFileInput = () => {
    const [field, , helpers] = useField({ name: 'receipts' });
    const { name, value } = field;
    // const { touched, error } = meta;
    const { setValue } = helpers;
    const [clientErrorAlert, setClientErrorAlert] = useState({
        isOpen: false,
        message: ''
    });
    const [serverErrorAlert, setServerErrorAlert] = useState({
        isOpen: false,
        message: ''
    });

    const handleChange = async ({ currentTarget: input }) => {
        if (!input.files?.length) return;

        const validReceiptFiles = [];
        for (const file of input.files) {
            try {
                await receiptFileValidation.validate(file);
                if (value.length + validReceiptFiles.length < MAX_NUMBER_OF_RECEIPT_FILES) {
                    file.localId = uuidv4();
                    validReceiptFiles.push(file);
                }
            } catch (error) {
                if (error instanceof Yup.ValidationError && !clientErrorAlert.isOpen) {
                    return setClientErrorAlert({ isOpen: true, message: error.message });
                }
                if (error?.response?.status >= 500 && !serverErrorAlert.isOpen) {
                    return setServerErrorAlert({
                        isOpen: true,
                        message: error.response.data.message
                    });
                }
            }
        }

        const receipts = value.concat(validReceiptFiles);
        setValue(receipts);
    };

    const isDisabledInput = value.length === MAX_NUMBER_OF_RECEIPT_FILES;

    return (
        <>
            <Box
                component="label"
                className="transaction-details__receipt-label"
                {...(isDisabledInput ? { sx: { border: '1px dashed #c0c7cc', cursor: 'default' } } : { htmlFor: name })}>
                <Box>
                    <Box component="span" className="transaction-details__receipt-label-icon-container" marginRight="10px">
                        <Box component="span">
                            <UploadIcon />
                        </Box>
                    </Box>
                    <Box component="span" fontWeight="500" fontSize="13px">
                        Upload{' '}
                    </Box>
                    <Box component="span" fontSize="10px" color="var(--secondary-color)">
                        PDF, JPEG, PNG | Max 25 mb and up to 5 files
                    </Box>
                </Box>
            </Box>
            <input
                id={name}
                name={name}
                type="file"
                accept=".png,.jpeg,.pdf"
                multiple
                onChange={handleChange}
                disabled={isDisabledInput}
                style={{ display: 'none' }}
            />
            {clientErrorAlert.isOpen && (
                <Alert
                    className="defi-alert"
                    icon={<CancelOutlined fontSize="small" color="error" />}
                    severity="error"
                    sx={{ marginTop: '8px' }}
                    onClose={() => setClientErrorAlert({ isOpen: false, message: '' })}>
                    {clientErrorAlert.message}
                </Alert>
            )}
            {serverErrorAlert.isOpen && (
                <Alert
                    className="defi-alert"
                    icon={<CancelOutlined fontSize="small" color="error" />}
                    severity="error"
                    sx={{ marginTop: '8px' }}
                    onClose={() => setServerErrorAlert({ isOpen: false, message: '' })}>
                    {serverErrorAlert.message}
                </Alert>
            )}
        </>
    );
};

export default ReceiptFileInput;
